import type { GameState, ScoreState } from "../game/types";

interface UISelectors {
  hud: string;
  speedValue: string;
  scoreValue: string;
  comboValue: string;
  livesValue: string;
  lanePressure: string;
  menuOverlay: string;
  pauseOverlay: string;
  gameOverOverlay: string;
  finalScore: string;
  bestScore: string;
  overtakes: string;
  nearMisses: string;
  penalties: string;
  startButton: string;
  restartButton: string;
  muteButton: string;
}

export class GameUI {
  private readonly hud: HTMLElement;
  private readonly speedValue: HTMLElement;
  private readonly scoreValue: HTMLElement;
  private readonly comboValue: HTMLElement;
  private readonly livesValue: HTMLElement;
  private readonly lanePressure: HTMLElement;
  private readonly menuOverlay: HTMLElement;
  private readonly pauseOverlay: HTMLElement;
  private readonly gameOverOverlay: HTMLElement;
  private readonly finalScore: HTMLElement;
  private readonly bestScore: HTMLElement;
  private readonly overtakes: HTMLElement;
  private readonly nearMisses: HTMLElement;
  private readonly penalties: HTMLElement;
  private readonly startButton: HTMLButtonElement;
  private readonly restartButton: HTMLButtonElement;
  private readonly muteButton: HTMLButtonElement;

  private startHandler: (() => void) | null = null;
  private restartHandler: (() => void) | null = null;
  private muteHandler: ((muted: boolean) => void) | null = null;
  private muted = true;

  constructor(selectors: UISelectors) {
    this.hud = this.query(selectors.hud);
    this.speedValue = this.query(selectors.speedValue);
    this.scoreValue = this.query(selectors.scoreValue);
    this.comboValue = this.query(selectors.comboValue);
    this.livesValue = this.query(selectors.livesValue);
    this.lanePressure = this.query(selectors.lanePressure);
    this.menuOverlay = this.query(selectors.menuOverlay);
    this.pauseOverlay = this.query(selectors.pauseOverlay);
    this.gameOverOverlay = this.query(selectors.gameOverOverlay);
    this.finalScore = this.query(selectors.finalScore);
    this.bestScore = this.query(selectors.bestScore);
    this.overtakes = this.query(selectors.overtakes);
    this.nearMisses = this.query(selectors.nearMisses);
    this.penalties = this.query(selectors.penalties);
    this.startButton = this.query<HTMLButtonElement>(selectors.startButton);
    this.restartButton = this.query<HTMLButtonElement>(selectors.restartButton);
    this.muteButton = this.query<HTMLButtonElement>(selectors.muteButton);

    this.startButton.addEventListener("click", () => this.startHandler?.());
    this.restartButton.addEventListener("click", () => this.restartHandler?.());
    this.muteButton.addEventListener("click", () => {
      this.setMuted(!this.muted);
      this.muteHandler?.(this.muted);
    });
  }

  onStart(handler: () => void): void {
    this.startHandler = handler;
  }

  onRestart(handler: () => void): void {
    this.restartHandler = handler;
  }

  onMuteToggle(handler: (muted: boolean) => void): void {
    this.muteHandler = handler;
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    this.muteButton.textContent = muted ? "Sound Off" : "Sound On";
    this.muteButton.setAttribute("aria-pressed", String(!muted));
  }

  updateHud(
    speedKmh: number,
    score: ScoreState,
    lanePressure: number[],
    lives: number,
    maxLives: number
  ): void {
    this.speedValue.textContent = Math.round(speedKmh).toString();
    this.scoreValue.textContent = Math.floor(score.score).toLocaleString("en-US");
    this.comboValue.textContent = `x${score.combo}`;
    this.livesValue.textContent = `${lives}/${maxLives}`;
    this.livesValue.classList.toggle("danger-text", lives <= 1);
    this.lanePressure.replaceChildren(
      ...lanePressure.map((pressure) => {
        const element = document.createElement("span");
        element.style.transform = `scaleY(${0.25 + pressure * 0.75})`;
        if (pressure > 0.72) element.className = "danger";
        else if (pressure > 0.45) element.className = "warning";
        return element;
      })
    );
  }

  setState(state: GameState, score: ScoreState): void {
    this.hud.classList.toggle("is-hidden", state === "menu" || state === "gameOver");
    this.menuOverlay.classList.toggle("active", state === "menu");
    this.pauseOverlay.classList.toggle("active", state === "paused");
    this.gameOverOverlay.classList.toggle("active", state === "gameOver");

    if (state === "gameOver") {
      this.finalScore.textContent = `${Math.floor(score.score).toLocaleString("en-US")} pts`;
      this.bestScore.textContent = Math.floor(score.bestScore).toLocaleString("en-US");
      this.overtakes.textContent = score.overtakes.toString();
      this.nearMisses.textContent = score.nearMisses.toString();
      this.penalties.textContent = Math.floor(score.penalties).toString();
    }
  }

  private query<T extends HTMLElement = HTMLElement>(selector: string): T {
    const element = document.querySelector<T>(selector);
    if (!element) {
      throw new Error(`Missing UI element: ${selector}`);
    }
    return element;
  }
}
